import axios from "axios";

import { baseUrl } from "../api";
import store from "../app/store";

const missingBaseUrl = (process.env.REACT_APP_PRICE_DIRECTUS_URL || "").replace(/\/$/, "");
const missingAccessToken = process.env.REACT_APP_PRICE_DIRECTUS_TOKEN || "";
const gatewayBaseUrl = (process.env.REACT_APP_API_URL || baseUrl).replace(/\/$/, "");
const pendingRetailerIdPlaceholder = "__PENDING_RETAILER_ID__";
const beefOverrideResolveDestination = "AI";
const createdRetailersByNameCache = new Map();

function getPortalToken() {
  return store.getState().user?.access_token;
}

export function getCurrentRetailerId() {
  return store.getState().retailer?.id || "";
}

export function buildGatewayAdminRecordUrl(collection, id) {
  if (!collection || !id) return "";
  return `${gatewayBaseUrl}/admin/content/${collection}/${id}`;
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function normalizeRetailerNameKey(value) {
  return String(value || "").trim().toLowerCase();
}

function formatRetailerOptionLabel(name, status) {
  const baseName = String(name || "").trim();
  const trimmedStatus = String(status || "").trim();
  if (!trimmedStatus || trimmedStatus.toLowerCase() === "published") {
    return baseName;
  }
  return `${baseName} (${trimmedStatus})`;
}

function isForbiddenError(error) {
  return error?.response?.status === 403;
}

function isForbiddenFieldError(error, fieldName) {
  const errors = Array.isArray(error?.response?.data?.errors) ? error.response.data.errors : [];
  return errors.some((entry) =>
    String(entry?.message || entry?.extensions?.reason || "").includes(`"${fieldName}"`)
  );
}

function missingApiHeaders() {
  return {
    "Content-Type": "application/json",
  };
}

export function missingReportsConfigured() {
  return Boolean(missingBaseUrl && missingAccessToken);
}

export function getMissingStatusChoices() {
  return ["pending", "auto_resolved", "resolved", "rejected"];
}

export function getMissingCsvUrl(type) {
  const path =
    type === "barcode"
      ? "/webhook-listener/barcode-missing-csv"
      : "/webhook-listener/beef-overrides-missing-csv";

  if (!missingBaseUrl) return "";

  const tokenParam = missingAccessToken ? `?access_token=${encodeURIComponent(missingAccessToken)}` : "";
  return `${missingBaseUrl}${path}${tokenParam}`;
}

async function missingGet(path, params = {}) {
  if (!missingBaseUrl) {
    throw new Error(
      "Missing reports API URL is not configured. Set REACT_APP_PRICE_DIRECTUS_URL."
    );
  }

  const res = await axios.get(`${missingBaseUrl}${path}`, {
    headers: missingApiHeaders(),
    params: {
      access_token: missingAccessToken,
      ...params,
    },
  });

  return res.data;
}

async function missingPatch(path, payload) {
  if (!missingBaseUrl) {
    throw new Error(
      "Missing reports API URL is not configured. Set REACT_APP_PRICE_DIRECTUS_URL."
    );
  }

  const res = await axios.patch(`${missingBaseUrl}${path}`, payload, {
    headers: missingApiHeaders(),
    params: {
      access_token: missingAccessToken,
    },
  });

  return res.data?.data;
}

function getApiErrorMessage(error, fallbackMessage) {
  const firstMessage = error?.response?.data?.errors?.[0]?.message;
  return firstMessage || fallbackMessage;
}

function ensureGatewayToken() {
  const token = getPortalToken();
  if (!token) {
    throw new Error("Missing session token for Gateway request.");
  }
  return token;
}

async function gatewayPost(path, payload) {
  const token = ensureGatewayToken();
  const res = await axios.post(`${gatewayBaseUrl}${path}`, payload, {
    params: { access_token: token },
  });
  return res.data?.data;
}

async function gatewayPatch(path, payload) {
  const token = ensureGatewayToken();
  const res = await axios.patch(`${gatewayBaseUrl}${path}`, payload, {
    params: { access_token: token },
  });
  return res.data?.data;
}

function parseSourceImageUrl(taskRun) {
  const firstInput = Array.isArray(taskRun?.input) ? taskRun.input[0] : null;
  const inputUrl = firstInput?.kwargs?.content?.[0]?.image_url?.url;
  if (inputUrl) return inputUrl;

  const firstMessage = Array.isArray(taskRun?.output?.messages) ? taskRun.output.messages[0] : null;
  const outputUrl = firstMessage?.kwargs?.content?.[0]?.image_url?.url;
  return outputUrl || "";
}

export async function fetchMissingRows(
  collection,
  {
    page = 1,
    limit = 10,
    status,
    retailerId,
    includeMissingUnknownRetailers = false,
    sortBy = "created_desc",
  } = {}
) {
  const andFilterClauses = [];

  if (status && status !== "all") {
    andFilterClauses.push({ status: { _eq: status } });
  }

  const trimmedRetailerId = String(retailerId || "").trim();
  const selectedRetailerClause = trimmedRetailerId
    ? {
        retailer_ref_id: {
          _eq: trimmedRetailerId,
        },
      }
    : null;
  const missingUnknownRetailerClauses = includeMissingUnknownRetailers
    ? [
        { retailer_ref_id: { _null: true } },
        { retailer_ref_id: { _empty: true } },
        { retailer: { _null: true } },
        { retailer: { _empty: true } },
      ]
    : [];

  if (selectedRetailerClause && missingUnknownRetailerClauses.length) {
    andFilterClauses.push({
      _or: [selectedRetailerClause, ...missingUnknownRetailerClauses],
    });
  } else if (selectedRetailerClause) {
    andFilterClauses.push(selectedRetailerClause);
  } else if (missingUnknownRetailerClauses.length) {
    andFilterClauses.push({
      _or: missingUnknownRetailerClauses,
    });
  }

  const sortMap = {
    updated_desc: "-date_updated",
    updated_asc: "date_updated",
    created_desc: "-date_created",
    created_asc: "date_created",
    retailer_asc: "retailer",
    retailer_desc: "-retailer",
    override_name_asc: "override_name",
    override_name_desc: "-override_name",
  };
  const sort = sortMap[sortBy] || sortMap.created_desc;
  const filter = andFilterClauses.length ? { _and: andFilterClauses } : {};

  const data = await missingGet(`/items/${collection}`, {
    page,
    limit,
    sort,
    "meta[]": "filter_count",
    fields:
      collection === "barcode_missing"
        ? [
            "id",
            "code",
            "raw_barcode",
            "product_name",
            "normalized_product_name",
            "retailer",
            "retailer_ref_id",
            "task_run_id",
            "status",
            "date_created",
            "date_updated",
          ]
        : [
            "id",
            "override_name",
            "normalized_product_name",
            "retailer",
            "retailer_ref_id",
            "ref_id",
            "task_run_id",
            "status",
            "date_created",
            "date_updated",
          ],
    filter,
  });

  return {
    rows: data?.data || [],
    total: data?.meta?.filter_count ?? 0,
  };
}

export async function fetchMissingProgress(collection) {
  const [allRowsRes, resolvedRowsRes] = await Promise.all([
    missingGet(`/items/${collection}`, {
      aggregate: { count: "*" },
      limit: 1,
    }),
    missingGet(`/items/${collection}`, {
      aggregate: { count: "*" },
      filter: { status: { _eq: "resolved" } },
      limit: 1,
    }),
  ]);

  const total = Number(allRowsRes?.data?.[0]?.count ?? 0);
  const resolved = Number(resolvedRowsRes?.data?.[0]?.count ?? 0);
  const remaining = Math.max(total - resolved, 0);

  return { total, resolved, remaining };
}

export async function fetchTaskRunDetails(taskRunId) {
  if (!taskRunId) return null;

  const row = await missingGet(`/items/workflow_task_run/${taskRunId}`, {
    fields: [
      "id",
      "status",
      "date_created",
      "input",
      "output",
      "run_id.id",
      "run_id.workflow_id.id",
      "run_id.workflow_id.name",
    ],
  });

  const taskRun = row?.data || null;
  if (!taskRun) return null;

  return {
    ...taskRun,
    sourceImageUrl: parseSourceImageUrl(taskRun),
  };
}

export async function updateMissingStatus(collection, id, status) {
  return missingPatch(`/items/${collection}/${id}`, { status });
}

export async function lookupGatewayRetailerIdByName(name) {
  // Retailer translation/name fields are not readable for some roles.
  // Keep this function as no-op to avoid forbidden lookups.
  return "";
}

export async function lookupGatewayRetailerIdById(id) {
  if (!id?.trim()) return "";
  const token = getPortalToken();
  if (!token) return "";

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/retailer`, {
      params: {
        access_token: token,
        limit: 1,
        fields: ["id"],
        filter: {
          _and: [
            {
              id: {
                _eq: id.trim(),
              },
            },
            {
              type: {
                _eq: "retailer",
              },
            },
          ],
        },
      },
    });

    return res?.data?.data?.[0]?.id || "";
  } catch (error) {
    if (isForbiddenError(error)) {
      return "";
    }
    throw error;
  }
}

export async function lookupGatewayRetailerById(id) {
  if (!id?.trim()) return null;
  const token = getPortalToken();
  if (!token) return null;

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/retailer`, {
      params: {
        access_token: token,
        limit: 1,
        fields: ["id", "status", "subdomain", "translations.retailer_name"],
        filter: {
          _and: [
            {
              id: {
                _eq: id.trim(),
              },
            },
            {
              type: {
                _eq: "retailer",
              },
            },
          ],
        },
      },
    });

    const row = res?.data?.data?.[0];
    if (!row?.id) {
      return null;
    }

    const translations = Array.isArray(row?.translations) ? row.translations : [];
    const retailerName =
      translations.find((entry) => String(entry?.retailer_name || "").trim())?.retailer_name || "";

    return {
      id: row.id,
      name: String(retailerName || "").trim() || row?.subdomain || "",
      status: String(row?.status || "").trim(),
    };
  } catch (error) {
    if (isForbiddenError(error) || error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function resolveGatewayRetailerIdFromRetailerRefId(retailerRefId) {
  const id = (retailerRefId || "").trim();
  if (!id) return "";
  return await lookupGatewayRetailerIdById(id);
}

export async function resolveGatewayRetailerIdFromKnowledgeRetailerId(knowledgeRetailerId) {
  // Backwards-compatible alias; queue rows now provide retailer_ref_id directly.
  return await resolveGatewayRetailerIdFromRetailerRefId(knowledgeRetailerId);
}

export async function resolveGatewayRetailerContextForMissingRow({
  retailerRefId = "",
  rawRetailerName = "",
} = {}) {
  const refId = String(retailerRefId || "").trim();
  const rawName = String(rawRetailerName || "").trim();

  if (refId) {
    const byRefRetailer = await lookupGatewayRetailerById(refId);
    if (byRefRetailer?.id) {
      return {
        retailerId: byRefRetailer.id,
        retailerName: byRefRetailer.name || rawName,
        source: "retailer_ref_id",
        hasRetailerRefId: true,
        exactNameMatchFound: false,
      };
    }

    if (rawName) {
      const rawNameKey = normalizeRetailerNameKey(rawName);
      const matches = await searchGatewayRetailers(rawName);
      const exactMatch = matches.find(
        (option) => normalizeRetailerNameKey(option?.name || option?.label) === rawNameKey
      );

      if (exactMatch?.id) {
        return {
          retailerId: exactMatch.id,
          retailerName: exactMatch.name || exactMatch.label || rawName,
          source: "retailer_ref_id_unresolved_exact_raw_retailer_match",
          hasRetailerRefId: true,
          exactNameMatchFound: true,
        };
      }
    }

    return {
      retailerId: "",
      retailerName: rawName,
      source: "retailer_ref_id_unresolved",
      hasRetailerRefId: true,
      exactNameMatchFound: false,
    };
  }

  if (rawName) {
    const rawNameKey = normalizeRetailerNameKey(rawName);
    const matches = await searchGatewayRetailers(rawName);
    const exactMatch = matches.find(
      (option) => normalizeRetailerNameKey(option?.name || option?.label) === rawNameKey
    );

    if (exactMatch?.id) {
      return {
        retailerId: exactMatch.id,
        retailerName: exactMatch.name || exactMatch.label || rawName,
        source: "exact_raw_retailer_match",
        hasRetailerRefId: false,
        exactNameMatchFound: true,
      };
    }
  }

  return {
    retailerId: "",
    retailerName: rawName,
    source: "none",
    hasRetailerRefId: false,
    exactNameMatchFound: false,
  };
}

export async function searchGatewayRetailers(query) {
  const searchQuery = String(query || "").trim();
  if (!searchQuery) {
    return [];
  }

  const token = getPortalToken();
  if (!token) {
    return [];
  }

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/retailer`, {
      params: {
        access_token: token,
        limit: 25,
        fields: ["id", "status", "subdomain", "translations.retailer_name"],
        "filter[_and][0][translations][retailer_name][_contains]": searchQuery,
        "filter[_and][1][type][_eq]": "retailer",
      },
    });

    const rows = res?.data?.data || [];
    if (!rows.length) {
      return [];
    }

    const seen = new Set();
    const options = [];
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const id = row?.id;
      if (!id || seen.has(id)) {
        continue;
      }

      seen.add(id);
      const translations = Array.isArray(row?.translations) ? row.translations : [];
      const retailerName =
        translations.find((entry) => String(entry?.retailer_name || "").trim())?.retailer_name || "";
      const name = String(retailerName || "").trim() || row?.subdomain || `Retailer ${index + 1}`;
      const status = String(row?.status || "").trim();
      const label = formatRetailerOptionLabel(name, status);

      options.push({
        id,
        name,
        status,
        label,
      });
    }

    return options;
  } catch (error) {
    if (
      isForbiddenError(error) ||
      error?.response?.status === 400 ||
      error?.response?.status === 404
    ) {
      return [];
    }
    throw error;
  }
}

export async function lookupGatewayBeefProductIdByRefId(refId) {
  if (!refId?.trim()) return "";
  const token = getPortalToken();
  if (!token) return "";

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/beef_product`, {
      params: {
        access_token: token,
        limit: 1,
        fields: ["id"],
        filter: {
          id: {
            _eq: refId.trim(),
          },
        },
      },
    });

    return res?.data?.data?.[0]?.id || "";
  } catch (error) {
    if (isForbiddenError(error)) {
      return "";
    }
    throw error;
  }
}

export async function lookupGatewayBeefProductIdByName(name) {
  // Product name fields are not readable for some roles.
  // Keep this function as no-op to avoid forbidden lookups.
  return "";
}

export async function lookupGatewayBeefProductIdByBarcodeCode(code) {
  if (!code?.trim()) return "";
  const token = getPortalToken();
  if (!token) return "";

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/barcode`, {
      params: {
        access_token: token,
        limit: 1,
        fields: ["id", "beef_product_id"],
        filter: {
          code: {
            _eq: code.trim(),
          },
        },
      },
    });

    return res?.data?.data?.[0]?.beef_product_id || "";
  } catch (error) {
    if (isForbiddenError(error)) {
      return "";
    }
    throw error;
  }
}

export async function lookupGatewayBarcodeByCode(code) {
  if (!code?.trim()) {
    return null;
  }
  const token = getPortalToken();
  if (!token) {
    return null;
  }

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/barcode`, {
      params: {
        access_token: token,
        limit: 1,
        fields: ["id", "beef_product_id"],
        filter: {
          code: {
            _eq: code.trim(),
          },
        },
      },
    });

    const row = res?.data?.data?.[0];
    if (!row?.id) {
      return null;
    }

    return {
      id: row.id,
      beefProductId: row.beef_product_id || "",
    };
  } catch (error) {
    if (isForbiddenError(error) || error?.response?.status === 400) {
      return null;
    }
    throw error;
  }
}

export async function lookupGatewayOverrideByName({
  overrideName = "",
  retailerId = "",
  beefProductId = "",
} = {}) {
  const name = String(overrideName || "").trim();
  if (!name) {
    return null;
  }
  const token = getPortalToken();
  if (!token) {
    return null;
  }

  const andClauses = [
    {
      name: {
        _eq: name,
      },
    },
  ];

  if (retailerId?.trim()) {
    andClauses.push({
      beef_product_overrides_id: {
        retailer: {
          _eq: retailerId.trim(),
        },
      },
    });
  }

  if (beefProductId?.trim()) {
    andClauses.push({
      beef_product_overrides_id: {
        beef_product: {
          _eq: beefProductId.trim(),
        },
      },
    });
  }

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/beef_product_overrides_translations`, {
      params: {
        access_token: token,
        limit: 1,
        fields: ["id", "name", "beef_product_overrides_id.id"],
        filter: {
          _and: andClauses,
        },
      },
    });

    const row = res?.data?.data?.[0];
    const overrideId = row?.beef_product_overrides_id?.id || "";
    if (!overrideId) {
      return null;
    }

    return {
      id: overrideId,
      translationId: row.id || "",
      name: row.name || name,
    };
  } catch (error) {
    if (isForbiddenError(error) || error?.response?.status === 400) {
      return null;
    }
    throw error;
  }
}

function mapBeefProductOptions(rows = []) {
  function getLanguageRank(row) {
    const code = String(row?.languages_id || row?.languages_code || "").toLowerCase();
    if (!code) return 2;
    if (code === "en-us") return 0;
    if (code.startsWith("en")) return 1;
    return 2;
  }

  const seen = new Set();
  const options = [];
  const sortedRows = [...rows].sort((a, b) => getLanguageRank(a) - getLanguageRank(b));

  for (const row of sortedRows) {
    const beefProductField = row?.beef_product_id;
    const beefProduct =
      beefProductField && typeof beefProductField === "object" ? beefProductField : null;
    const id = beefProduct?.id || (typeof beefProductField === "string" ? beefProductField : "");
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const hierarchy = String(beefProduct?.hierarchy || "").trim();
    const baseLabel = row?.name || `Matched product ${options.length + 1}`;
    const label = hierarchy ? `${baseLabel} (${hierarchy})` : baseLabel;

    options.push({ id, label, hierarchy });
  }

  return options;
}

function buildBeefProductNameFilter(productName, normalizedProductName) {
  const candidates = [productName, normalizedProductName]
    .map((value) => (value || "").trim())
    .filter(Boolean);

  if (!candidates.length) return null;

  const orClauses = candidates.map((term) => ({
    name: {
      _icontains: term,
    },
  }));

  return {
    _or: orClauses,
  };
}

function buildBeefProductTranslationsFilter(productName, normalizedProductName) {
  const nameFilter = buildBeefProductNameFilter(productName, normalizedProductName);
  if (!nameFilter) return null;

  return {
    _and: [
      nameFilter,
      {
        beef_product_id: {
          _null: false,
        },
      },
    ],
  };
}

function buildEnglishBeefProductTranslationsFilter(productName, normalizedProductName) {
  const baseFilter = buildBeefProductTranslationsFilter(productName, normalizedProductName);
  if (!baseFilter) return null;

  return {
    _and: [
      ...(baseFilter?._and || []),
      {
        languages_id: {
          _starts_with: "en",
        },
      },
    ],
  };
}

async function fetchGatewayBeefProductTranslationRows(
  token,
  productName,
  normalizedProductName = ""
) {
  const englishFilter = buildEnglishBeefProductTranslationsFilter(productName, normalizedProductName);
  if (!englishFilter) return [];

  try {
    const res = await axios.get(`${gatewayBaseUrl}/items/beef_product_translations`, {
      params: {
        access_token: token,
        limit: 50,
        fields: [
          "id",
          "name",
          "languages_id",
          "beef_product_id",
          "beef_product_id.id",
          "beef_product_id.hierarchy",
        ],
        filter: englishFilter,
      },
    });

    return res?.data?.data || [];
  } catch (error) {
    if (!isForbiddenFieldError(error, "languages_id")) {
      throw error;
    }

    const fallbackFilter = buildBeefProductTranslationsFilter(productName, normalizedProductName);
    if (!fallbackFilter) return [];

    const fallbackRes = await axios.get(`${gatewayBaseUrl}/items/beef_product_translations`, {
      params: {
        access_token: token,
        limit: 50,
        fields: ["id", "name", "beef_product_id", "beef_product_id.id", "beef_product_id.hierarchy"],
        filter: fallbackFilter,
      },
    });

    return fallbackRes?.data?.data || [];
  }
}

export async function findGatewayBeefProductsByNames(productName, normalizedProductName) {
  const token = getPortalToken();
  if (!token) return [];

  try {
    const rows = await fetchGatewayBeefProductTranslationRows(token, productName, normalizedProductName);
    return mapBeefProductOptions(rows);
  } catch (error) {
    if (isForbiddenError(error) || error?.response?.status === 400) {
      return [];
    }
    throw error;
  }
}

export async function searchGatewayBeefProducts(query, normalizedQuery = "") {
  const token = getPortalToken();
  if (!token) return [];

  try {
    const rows = await fetchGatewayBeefProductTranslationRows(token, query, normalizedQuery);
    return mapBeefProductOptions(rows);
  } catch (error) {
    if (isForbiddenError(error) || error?.response?.status === 400) {
      return [];
    }
    throw error;
  }
}

export async function resolveBarcodeMissingRecord(record, formValues) {
  const createRetailerIfMissing = Boolean(formValues.createRetailerIfMissing);
  let ownerId = formValues.ownerId || null;
  const beefProductId = formValues.beefProductId || "";

  if (!ownerId) {
    if (!createRetailerIfMissing) {
      throw new Error("Retailer not found. Select a valid retailer or create a new one.");
    }
    ownerId = await createGatewayRetailerFromMissing(record, formValues.retailerName || "");
  }

  if (!beefProductId) {
    throw new Error("Beef product is required to create barcode.");
  }

  const payload = buildBarcodeResolvePayload(record, formValues, ownerId);

  if (!payload.owner || !payload.beef_product_id) {
    throw new Error("Gateway owner (retailer) and beef product are required to create barcode.");
  }

  try {
    await gatewayPost("/items/barcode", payload);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to create barcode in Gateway."));
  }
}

export async function resolveOverrideMissingRecord(record, formValues) {
  const beefProductId = formValues.beefProductId || "";
  let retailerId = formValues.retailerId || "";
  const overrideName = formValues.overrideName || record.override_name || "";
  const createRetailerIfMissing = Boolean(formValues.createRetailerIfMissing);

  if (!retailerId) {
    if (!createRetailerIfMissing) {
      throw new Error("Retailer not found. Select a valid retailer or create a new one.");
    }
    retailerId = await createGatewayRetailerFromMissing(record, formValues.retailerName || "");
  }

  if (!beefProductId || !retailerId || !overrideName.trim()) {
    throw new Error("Gateway retailer, beef product, and override name are required.");
  }

  const payload = buildOverrideResolvePayload(record, formValues, retailerId);

  try {
    await gatewayPatch(`/items/beef_product/${beefProductId}`, payload);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to create product override in Gateway."));
  }
}

function buildBarcodeResolvePayload(record, formValues, ownerId) {
  return {
    code: formValues.code || record.code,
    raw_retailer: record.retailer || null,
    raw_description: record.product_name || null,
    data_source: "Canada Beef Research",
    status: "published",
    owner: ownerId,
    beef_product_id: formValues.beefProductId || null,
  };
}

function buildOverrideResolvePayload(record, formValues, retailerId) {
  const overrideName = formValues.overrideName || record.override_name || "";

  return {
    beef_product_overrides: {
      create: [
        {
          status: "published",
          retailer: retailerId,
          destination: beefOverrideResolveDestination,
          translations: {
            create: [
              {
                languages_code: "en-US",
                name: overrideName.trim(),
              },
            ],
            update: [],
            delete: [],
          },
        },
      ],
      update: [],
      delete: [],
    },
  };
}

export function getBarcodeResolveGatewayRequestPreview(record, formValues) {
  if (!record) {
    return null;
  }

  const ownerId =
    formValues.ownerId ||
    (formValues.createRetailerIfMissing ? pendingRetailerIdPlaceholder : null);
  const payload = buildBarcodeResolvePayload(record, formValues, ownerId);

  return {
    method: "POST",
    url: `${gatewayBaseUrl}/items/barcode`,
    payload,
    blockedReason:
      !payload.owner || !payload.beef_product_id
        ? "Retailer and beef product are required before this request can be sent."
        : "",
  };
}

export function getOverrideResolveGatewayRequestPreview(record, formValues) {
  if (!record) {
    return null;
  }

  const retailerId =
    formValues.retailerId ||
    (formValues.createRetailerIfMissing ? pendingRetailerIdPlaceholder : "");
  const beefProductId = formValues.beefProductId || "";
  const payload = buildOverrideResolvePayload(record, formValues, retailerId);

  return {
    method: "PATCH",
    url: `${gatewayBaseUrl}/items/beef_product/${beefProductId || "<beef_product_id>"}`,
    payload,
    blockedReason:
      !retailerId || !beefProductId || !String(formValues.overrideName || record.override_name || "").trim()
        ? "Retailer, beef product, and override name are required before this request can be sent."
        : "",
  };
}

export async function createGatewayRetailerFromMissing(record, retailerNameInput = "") {
  const retailerName = String(retailerNameInput || record?.retailer || "Missing Retailer").trim();
  const retailerNameKey = normalizeRetailerNameKey(retailerName);
  if (retailerNameKey && createdRetailersByNameCache.has(retailerNameKey)) {
    return createdRetailersByNameCache.get(retailerNameKey) || "";
  }

  // Avoid creating duplicate retailers when multiple missing rows share the same retailer name.
  // This reuses an existing Gateway retailer when an exact name match is found.
  if (retailerName) {
    const matches = await searchGatewayRetailers(retailerName);
    const exactMatch = matches.find(
      (option) => normalizeRetailerNameKey(option?.name || option?.label) === retailerNameKey
    );
    if (exactMatch?.id) {
      if (retailerNameKey) {
        createdRetailersByNameCache.set(retailerNameKey, exactMatch.id);
      }
      return exactMatch.id;
    }
  }

  const subdomainBase = slugify(retailerName) || "missing-retailer";
  const suffix = Date.now().toString().slice(-6);
  const subdomain = `${subdomainBase}-${suffix}.missing.cdnbeef.ca`;

  const payload = {
    status: "draft",
    type: "retailer",
    subdomain,
    // Required Gateway retailer fields (no DB defaults).
    icon_color: "#FFFFFF",
    button_background_color: "#FFFFFF",
    translations: {
      create: [
        {
          languages_code: "en-US",
          retailer_name: retailerName,
        },
      ],
      update: [],
      delete: [],
    },
  };

  try {
    const createdRetailer = await gatewayPost("/items/retailer", payload);
    const id = createdRetailer?.id;
    if (!id) {
      throw new Error("Retailer creation succeeded but no ID returned.");
    }
    if (retailerNameKey) {
      createdRetailersByNameCache.set(retailerNameKey, id);
    }
    return id;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to create new retailer in Gateway."));
  }
}
